import { Injectable } from "@angular/core";
import { ClientFilter } from "src/app/model/client-filter";
import { InvoiceNumberBookPoint } from "src/app/model/invoice-number-book-point";
import { BillingInvoice } from "src/app/model/billing/billing-invoice";
import { BillingInvoiceCreateResponse } from "src/app/model/billing/billing-invoice-create-response";
import { BillingInvoiceEditInfo } from "src/app/model/billing/billing-invoice-edit-info";
import { ReportViewer } from "src/app/model/report-viewer";
import { BillingInvoiceItemEditInfo } from "../../../model/billing/billing-invoice-item-edit-info";
import { DataService } from "src/app/services/data.service";
import { PreBillingService } from "../pre-billing.service";
import { BillingInvoiceSearchItem } from "../../../model/billing/billing-invoice-search-item";
import { BillingInvoiceExport } from "src/app/model/billing/billing-invoice-export";
import { ClientSearch } from "../../../model/client-search";
import { BillingInvoiceLogItem } from "src/app/model/billing/billing-invoice-log-item";

@Injectable({
  providedIn: "root",
})
export class BillingInvoiceEntityService {
  constructor(
    public dataService: DataService,
    public preBillingService: PreBillingService,
  ) {}

  private readonly sendInvoiceMailEndPoint: string =
    '/billingInvoice/SendInvoiceMail/';
  private readonly getBillingInvoiceSearchItemsEndPoint: string =
    "/billingInvoice/getBillingInvoiceSearchItems/";
  private readonly getBillingInvoiceEditInfoEndPoint: string =
    "/billingInvoice/getBillingInvoiceEditInfo/";
  private readonly billingInvoiceCreateEndPoint: string =
    "/billingInvoice/billingInvoiceCreate/";
  private readonly getBillingInvoiceReportEndPoint: string =
    "/billingInvoice/getBillingInvoiceReport/";
  private readonly createExcelAdjustmentEndPoint: string =
    "/billingInvoice/createExcelAdjustment/";
  private readonly getBillingInvoiceExportEndPoint: string =
    "/billingInvoice/getBillingInvoiceExport/";
  private readonly billingInvoiceItemEditInfosAdjustmentEndPoint: string =
    "/billingInvoice/billingInvoiceItemEditInfosAdjustment/";
  private readonly getMedicalAppointmentProcedureIdsEndPoint: string =
    "/billingInvoice/getMedicalAppointmentProcedureIds/";

  async sendInvoiceMail(billingInvoiceId: number): Promise<void> {

    const params = { InvoiceId: billingInvoiceId };
    await this.dataService.post<void>(this.sendInvoiceMailEndPoint, params).toPromise();
  }

  async getBillingInvoiceLog(
    billingInvoiceId: number,
  ): Promise<BillingInvoiceLogItem[]> {
    const params = { billingInvoiceId: billingInvoiceId };
    return await this.dataService
      .get<
        BillingInvoiceLogItem[]
      >("/billingInvoice/GetBillingInvoiceLog/", params)
      .toPromise();
  }

  async getMedicalAppointmentProcedureIds(
    billingInvoiceId: number,
  ): Promise<number[]> {
    const params = { billingInvoiceId: billingInvoiceId };
    return await this.dataService
      .get<number[]>(this.getMedicalAppointmentProcedureIdsEndPoint, params)
      .toPromise();
  }

  async getBillingInvoiceSearchItem(
    erpCode: string,
    invoiceNumber?: number,
    invoiceDateFrom?: Date,
    invoiceDateTo?: Date,
    clientId?: number,
  ): Promise<BillingInvoiceSearchItem[]> {
    let toUtc = "";
    let fromUtc = "";

    if (invoiceDateFrom) {
      fromUtc = new Date(
        invoiceDateFrom.getFullYear(),
        invoiceDateFrom.getMonth(),
        invoiceDateFrom.getDate(),
      ).toDateString();
    }

    if (invoiceDateTo) {
      toUtc = new Date(
        invoiceDateTo.getFullYear(),
        invoiceDateTo.getMonth(),
        invoiceDateTo.getDate(),
      ).toDateString();
    }

    const params = {
      erpCode: erpCode,
      invoiceNumber: invoiceNumber,
      invoiceDateFrom: fromUtc,
      invoiceDateTo: toUtc,
      clientId: clientId,
    };

    return await this.dataService
      .get<
        BillingInvoiceSearchItem[]
      >(this.getBillingInvoiceSearchItemsEndPoint, params)
      .toPromise();
  }

  async billingInvoiceItemEditInfosAdjustment(
    base64File: string,
    items: BillingInvoiceItemEditInfo[],
  ): Promise<BillingInvoiceItemEditInfo[]> {
    const params = {
      FileBase64: base64File,
      BillingInvoiceItemEditInfos: items,
    };
    return await this.dataService
      .post<
        BillingInvoiceItemEditInfo[]
      >(this.billingInvoiceItemEditInfosAdjustmentEndPoint, params)
      .toPromise();
  }

  async createExcelAdjustment(
    items: BillingInvoiceItemEditInfo[],
  ): Promise<string> {
    return await this.dataService
      .post<string>(this.createExcelAdjustmentEndPoint, items)
      .toPromise();
  }

  async billingInvoiceCreate(
    preBillingId: number,
    clientSearch: ClientSearch,
    invoiceNumberBookPoint: InvoiceNumberBookPoint,
    billingInvoice: BillingInvoice,
    medicalAppointmentProcedureExcludeIds: number[],
    items: BillingInvoiceItemEditInfo[],
  ): Promise<BillingInvoiceCreateResponse> {
    const sendItems =
      items != null &&
      items.findIndex((i) => i.ValorizationItemAdjustment != null) > -1;
    const params = {
      PreBillingId: preBillingId,
      IsRejected: billingInvoice.IsRejected,
      IsCirculacionAbierta: billingInvoice.IsCirculacionAbierta,
      Description: billingInvoice.Description,
      InvoiceDate: billingInvoice.InvoiceDate,
      InvoiceExpiredDate: billingInvoice.InvoiceExpiredDate,
      BillingInvoicePeriod: billingInvoice.BillingInvoicePeriod,
      Amount: billingInvoice.Amount,
      InvoiceLabelType: billingInvoice.InvoiceLabelType,
      TaxpayerType: billingInvoice.TaxpayerType,
      InvoicePoint: billingInvoice.InvoicePoint,
      ClientSearch: clientSearch,
      InvoiceTaxes: billingInvoice.InvoiceTaxes,
      InvoiceNumberBookPoint: invoiceNumberBookPoint,
      MedicalAppointmentProcedureExcludeIds:
        medicalAppointmentProcedureExcludeIds,
      OriginalBillingInvoiceManualId:
        billingInvoice.OriginalBillingInvoiceManualId,
      OriginalBillingInvoiceId: billingInvoice.OriginalBillingInvoiceId,
      OriginalBillingInvoiceDate: billingInvoice.OriginalBillingInvoiceDate,
      OriginalAmount: billingInvoice.OriginalAmount,
      OriginalCompanyId: billingInvoice.OriginalCompanyId,
      Items: sendItems ? items : null,
    };

    return await this.dataService
      .post<BillingInvoiceCreateResponse>(
        this.billingInvoiceCreateEndPoint,
        params,
      )
      .toPromise();
  }

  async getBillingInvoiceEditInfo(
    preBillingId: number,
    sectorId: number,
  ): Promise<BillingInvoiceEditInfo> {
    const params = { preBillingId: preBillingId, sectorId: sectorId };
    return await this.dataService
      .get<BillingInvoiceEditInfo>(
        this.getBillingInvoiceEditInfoEndPoint,
        params,
      )
      .toPromise();
  }

  async getBillingInvoiceReport(
    billingInvoiceId: number,
  ): Promise<ReportViewer> {
    const params = { billingInvoiceId: billingInvoiceId };
    return await this.dataService
      .get<ReportViewer>(this.getBillingInvoiceReportEndPoint, params)
      .toPromise();
  }

  async getBillingInvoiceItemEditInfos(
    referenceId: number,
  ): Promise<BillingInvoiceItemEditInfo[]> {
    return await this.preBillingService.getPreBillingInvoiceItems(referenceId);
  }

  async getBillingInvoiceExport(
    billingInvoiceId: number,
    billingInvoiceExportId: number,
  ): Promise<BillingInvoiceExport> {
    const params = {
      billingInvoiceId: billingInvoiceId,
      billingInvoiceExportId: billingInvoiceExportId,
    };
    return await this.dataService
      .get<BillingInvoiceExport>(this.getBillingInvoiceExportEndPoint, params)
      .toPromise();
  }
}
